import styles from './StockTracing.module.css';
import React, { useState, useEffect } from 'react';
import { t } from 'i18next';
import Modal from 'react-modal';
import BankingForm from './BankingForm';

// 날짜 포맷팅
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    const ms = String(date.getMilliseconds()).padStart(3, '0');
    return `${y}-${m}-${d} ${hh}:${mm}:${ss}.${ms}`;
};

// ─── [폴더 구조용 재귀 컴포넌트] ───
const StockNode = ({ node, label, onNodeClick }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const hasChildren = node.children && node.children.length > 0;

    const handleToggle = (e) => {
        e.stopPropagation();
        setIsExpanded(prev => !prev);
    };

    return (
        <div className={styles.treeNodeWrapper}>
            <div className={styles.treeRow}>
                {/* 1. [+]/[-] 버튼 */}
                {hasChildren ? (
                    <div className={styles.expandBtn} onClick={handleToggle}>
                        {isExpanded ? '-' : '+'}
                    </div>
                ) : (
                    <div className={styles.expandPlaceholder}></div>
                )}

                {/* 2. 스탁 정보 박스 */}
                <div 
                    className={styles.box}
                    onClick={() => onNodeClick(node.Organoid_Info_Key, node.time)}
                >
                    <span className={styles.label}>{label}</span>
                    {node.PassageNo && (
                        <span className={styles.subLabel}>P: {node.PassageNo}</span>
                    )}
                </div>
            </div>

            {/* 3. 자식 재귀 렌더링 */}
            {hasChildren && isExpanded && (
                <div>
                    {node.children.map((child, idx) => (
                        <StockNode 
                            key={`${child.time}-${idx}`} 
                            node={child} 
                            label={`${t('Child Stock')} #${idx + 1}`}
                            onNodeClick={onNodeClick} 
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

function StockTracing({ time, item, onCancel }) {
    const [isLoading, setIsLoading] = useState(false);
    const [tracingMode, setTracingMode] = useState('parent'); 

    const [parentList, setParentList] = useState([]);
    const [childTree, setChildTree] = useState([]);
    
    // Root 정보 및 상태
    const [rootDetails, setRootDetails] = useState(item || null);
    const [isRootExpanded, setIsRootExpanded] = useState(true);

    const [editItem, setEditItem] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    // Root 정보 보정
    useEffect(() => {
        if (!time) return;
        if (item && item.Organoid_Info_Key) {
            setRootDetails(item);
        } else {
            fetch('/api/getAllParentsStock', { 
                method: 'post',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ time: time }),
            })
            .then(r => r.json())
            .then(data => {
                if (data.data && data.data.length > 0) {
                    setRootDetails(data.data[0]);
                }
            })
            .catch(e => console.error(e));
        }
    }, [time, item]);

    // 데이터 조회
    useEffect(() => {
        setIsLoading(true);
        let url = tracingMode === 'parent' ? '/api/getAllParentsStock' : '/api/getAllChildrenStock';

        fetch(url, {
            method: 'post',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ time: time }),
        })
            .then(r => r.json())
            .then(data => {
                if (tracingMode === 'parent') setParentList(data.data);
                else setChildTree(data.data);
                setIsLoading(false);
            })
            .catch(err => {
                console.error(err);
                setIsLoading(false);
            });
    }, [time, tracingMode]);

    // 박스 클릭 -> 상세 조회
    const handleBoxClick = async (organoidInfoKey, stockTime) => {
        if (!organoidInfoKey) {
            alert(t('스탁 정보를 불러오지 못했습니다.'));
            return;
        }

        const formattedTime = formatDate(stockTime);
        try {
            const res = await fetch('/api/getBankingList', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ param: organoidInfoKey })
            });
            const data = await res.json();
            const targetItem = data.find(i => formatDate(i.time) === formattedTime);

            if (targetItem) {
                setEditItem({ 
                    ...targetItem, 
                    Organoid_Info_Key: organoidInfoKey, 
                    mode: 'edit' 
                });
                setIsEditModalOpen(true);
            } else {
                alert(t('데이터를 찾을 수 없습니다.'));
            }
        } catch (e) {
            console.error(e);
            alert(t('상세 정보를 불러오는데 실패했습니다.'));
        }
    };

    // 저장 핸들러
    const handleSave = (newItem) => {
        fetch('/api/updateBankingInfo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ param: newItem }),
        })
        .then(res => res.json())
        .then(data => {
            if(data.message) alert(data.message);
            setIsEditModalOpen(false);
            setEditItem(null);
        })
        .catch(err => {
            console.error(err);
            alert('Error');
        });
    };

    return (
        <div className={styles.container}>
            {/* 상단 헤더 */}
            <div className={styles.header}>
                <h2 className={styles.title}>{t('Stock Tracing')}</h2>
                <div className={styles.tabGroup}>
                    <button 
                        className={`${styles.tab} ${tracingMode === 'parent' ? styles.tabActive : ''}`}
                        onClick={() => setTracingMode('parent')}
                    >
                        {t('Parent')}
                    </button>
                    <button 
                        className={`${styles.tab} ${tracingMode === 'child' ? styles.tabActive : ''}`}
                        onClick={() => setTracingMode('child')}
                    >
                        {t('Child')}
                    </button>
                </div>
            </div>
            
            <div className={styles.contentArea}>
                {isLoading ? (
                    <div className={styles.message}>{t('Loading...')}</div>
                ) : (
                    <>
                        {/* 1. 부모 추적 (Timeline) */}
                        {tracingMode === 'parent' && (
                            <div className={styles.timelineWrapper}>
                                {parentList && parentList.length > 0 ? (
                                    parentList.map((stock, index) => (
                                        <div key={index} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <div
                                                className={`${styles.box} ${index === 0 ? styles.currentBox : ''}`}
                                                style={{ width: '80%' }}
                                                onClick={() => handleBoxClick(stock.Organoid_Info_Key, stock.time)}
                                            >
                                                <span className={styles.label}>
                                                    {index === 0 ? t('Current Stock') : `${t('Parent Stock')} #${index}`}
                                                </span>
                                                {stock.PassageNo && (
                                                    <span className={styles.subLabel}>P: {stock.PassageNo}</span>
                                                )}
                                            </div>

                                            {index !== parentList.length - 1 && (
                                                <div className={styles.connector}>
                                                    <div className={styles.line}></div>
                                                    <div className={styles.arrow}></div>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className={styles.emptyMessage}>{t('No parent stocks found.')}</div>
                                )}
                            </div>
                        )}

                        {/* 2. 자식 추적 (Tree View) */}
                        {tracingMode === 'child' && (
                            <div className={styles.treeContainer}>
                                {/* Root 노드 */}
                                <div className={styles.rootNodeWrapper}>
                                    
                                    {/* 접기 버튼 */}
                                    {childTree && childTree.length > 0 ? (
                                        <div 
                                            className={styles.expandBtn} 
                                            onClick={() => setIsRootExpanded(!isRootExpanded)}
                                            style={{ marginRight: '8px' }}
                                        >
                                            {isRootExpanded ? '-' : '+'}
                                        </div>
                                    ) : (
                                        <div className={styles.expandPlaceholder} style={{ marginRight: '8px' }}></div>
                                    )}

                                    {/* 현재 스탁 박스 */}
                                    <div 
                                        className={`${styles.box} ${styles.currentBox}`} 
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            if (rootDetails) {
                                                handleBoxClick(rootDetails.Organoid_Info_Key, rootDetails.time);
                                            }
                                        }}
                                    >
                                        <span className={styles.label}>{t('Current Stock')}</span>
                                        {rootDetails?.PassageNo && (
                                            <span className={styles.subLabel}>P: {rootDetails.PassageNo}</span>
                                        )}
                                    </div>
                                </div>
                                
                                {/* [수정] JS 수직 연결선 div 제거 (CSS로 대체) */}
                                
                                {/* 자식 트리 렌더링 */}
                                {isRootExpanded && childTree && childTree.length > 0 ? (
                                    <div>
                                        {childTree.map((child, idx) => (
                                            <StockNode 
                                                key={`${child.time}-${idx}`} 
                                                node={child} 
                                                label={`${t('Child Stock')} #${idx + 1}`}
                                                onNodeClick={handleBoxClick} 
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    (isRootExpanded && (!childTree || childTree.length === 0)) && (
                                        <div className={styles.emptyMessage} style={{textAlign:'left', paddingLeft:'30px'}}>
                                            {t('No child stocks found.')}
                                        </div>
                                    )
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* 상세 모달 */}
            <Modal 
                isOpen={isEditModalOpen}
                onRequestClose={() => setIsEditModalOpen(false)}
                style={{
                    content: { width: 'fit-content', height: 'fit-content', margin: 'auto', zIndex: 1000 },
                    overlay: { zIndex: 999, backgroundColor: 'rgba(0,0,0,0.5)' }
                }}
            >
                <h2>Edit Banking Information</h2>
                {editItem && (
                    <BankingForm
                        mode="edit"
                        item={editItem}
                        isEditMode={true}
                        onSave={handleSave}
                        onCancel={() => setIsEditModalOpen(false)}
                        onDelete={() => {}} 
                        onRetrieve={() => {}}
                    />
                )}
            </Modal>
        </div>
    );
}

export default StockTracing;